import Loader from '@components/framework/loader/Loader';
import {State} from '@shared/store/reducers/Index';
import {useSnackbar} from 'notistack';
import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import './ClaimsTicketsPatientDetailsDrawer.scss';
import TicketingSystemGrid from '../../TicketingSystem/TicketingSystemGrid/TicketingSystemGrid';
import {getClinicUsersList} from '@shared/services/ClinicServices';
import {UserModelMapper} from '@shared/services/service-mappers/ModelMapper';
import {
  MESSAGES_EVENT_TYPE_MAPPER,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import {
  IClaimsTicketDetails,
  IWorkFlow
} from '@shared/services/service-mappers/WorkflowMapper';
import ClaimsTicketsDetailsFields from '../ClaimsTicketDetailsFields/ClaimsTicketDetailsFields';
import useClaimsTicketsFieldsData from '../hooks/useClaimsTicketsFieldsData';
import {getWorkflowDetails} from '@shared/services/WorkflowServices';

const ClaimsTicketsPatientDetailsDrawer = (): JSX.Element => {
  const {groupId, clinicId, patientDetails} = useSelector(
    (state: State) => state.patientDetailsDrawer
  );
  const [claimsTicketsWorkflow, setClaimsTicketsWorkflow] = useState({
    isLoading: false,
    data: null as null | IWorkFlow
  });
  useEffect(() => {
    const fetchClaimsWorkflow = async () => {
      try {
        setClaimsTicketsWorkflow(p => ({
          ...p,
          isLoading: true
        }));
        const response = await getWorkflowDetails({
          clinicGroupId: groupId,
          params: {
            module:
              TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER['CLAIMS_TICKETS']
          }
        });
        if (response.data.workflow) {
          setClaimsTicketsWorkflow({
            isLoading: false,
            data: response.data.workflow
          });
        } else {
          setClaimsTicketsWorkflow({
            isLoading: false,
            data: null
          });
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
        setClaimsTicketsWorkflow({
          isLoading: false,
          data: null
        });
      }
    };
    if (groupId) {
      fetchClaimsWorkflow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);
  const user = useSelector((state: State) => state.auth.user);
  const [commonState, setCommonState] = React.useState({
    isLoading: true,
    allClinicUsers: [] as UserModelMapper[]
  });
  const {clinicPayersList, customFields} = useClaimsTicketsFieldsData({
    clinicGroupId: groupId
  });

  const {enqueueSnackbar} = useSnackbar();

  const [ticketDetailsView, setTicketDetailsView] = useState<{
    show: boolean;
    ticketId?: number | null;
  }>({
    show: false
  });

  const updateTicketDetailsView = ({
    show,
    ticketId
  }: {
    show: boolean;
    ticketId?: number | null;
  }) => {
    setTicketDetailsView({show, ticketId});
  };

  useEffect(() => {
    const init = async () => {
      setCommonState(prev => ({
        ...prev,
        isLoading: true
      }));
      try {
        const response = await getClinicUsersList(groupId, clinicId);

        if (response.data) {
          setCommonState(prev => ({
            ...prev,
            allClinicUsers: response.data
          }));
        } else {
          setCommonState(prev => ({
            ...prev,
            allClinicUsers: []
          }));
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      } finally {
        setCommonState(prev => ({
          ...prev,
          isLoading: false
        }));
      }
    };
    if (groupId && clinicId) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, groupId]);
  const ticketFilters = useMemo(() => ({}), []);

  if (commonState.isLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }
  return (
    <div className={`prior-auth-patient-drawer`}>
      {claimsTicketsWorkflow.isLoading && <Loader />}
      {!claimsTicketsWorkflow.isLoading && claimsTicketsWorkflow.data && (
        <TicketingSystemGrid
          module={'CLAIMS_TICKETS'}
          groupPermission={'CLAIMS_TICKETS_ENABLE'}
          clinicGroupId={groupId}
          clinicId={clinicId}
          statuses={claimsTicketsWorkflow.data.status}
          allClinicUsers={commonState.allClinicUsers}
          workflowId={claimsTicketsWorkflow.data.id}
          ticketFilters={ticketFilters}
          patientId={patientDetails.patient_id}
          userId={user.id}
          renderTicketDetailsModuleInputFields={({
            isEditing,
            methods,
            ticketDetails,
            refreshTicketDetails,
            isLoadingTicketDetails
          }) => (
            <ClaimsTicketsDetailsFields
              isEditing={isEditing}
              methods={methods}
              clinicGroupId={groupId}
              ticketDetails={ticketDetails as IClaimsTicketDetails}
              refreshTicketDetails={refreshTicketDetails}
              customFields={customFields}
              clinicPayersList={clinicPayersList}
              isLoadingTicketDetails={isLoadingTicketDetails}
              clinicId={clinicId}
            />
          )}
          showPatientInfoInGridAndTicketDetails={false}
          messageEventType={MESSAGES_EVENT_TYPE_MAPPER.PRIOR_AUTHORIZATION}
          ticketDetailsView={ticketDetailsView}
          updateTicketDetailsView={updateTicketDetailsView}
        />
      )}
    </div>
  );
};

export default ClaimsTicketsPatientDetailsDrawer;
